import { Link, useParams } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Card, CardBody, Spinner } from '@fluxora/ui';

import { isNotFound } from '../api/bff-client';
import { useNewServiceStatus } from '../api/new-service.queries';
import {
  pendingServiceCopy,
  showsOverdueNotice,
} from '../services/pendingService.service';

/**
 * The anonymous pending-service page (E-SF20 / US-01).
 *
 * Reached at `/new-service/:ref` — the link in the confirmation email. NO login,
 * NO session: the unguessable ref IS the capability, exactly like `/status/:token`.
 * For the months between "we're building a house" and "the utility set a meter",
 * this page is the customer's entire relationship with us.
 *
 * All five async states are handled: no-ref guard, loading (Spinner), error (an
 * expired/unknown ref reads as an honest "this link isn't valid", not a crash),
 * empty is not applicable (a ref resolves to exactly one record), and the record.
 *
 * ── IT PROMISES NOTHING IT CANNOT DO ───────────────────────────────────────
 * There is no detection loop (US-02), so a record sits in `DECLARED` and this page
 * keeps saying the same thing. The copy therefore tells the customer to CHECK BACK
 * — which is true and actionable — and never that we will contact them, which is
 * not. `NewServicePendingPage.test.tsx` asserts the absence of a notify promise,
 * so a future copy edit that adds one fails the build.
 */
export function NewServicePendingPage(): JSX.Element {
  const { t } = useTranslation();
  const { ref } = useParams<{ ref: string }>();

  const status = useNewServiceStatus(ref ?? '');

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-10">
      <h1 className="text-xl font-bold text-primary">{t('shop.pendingService.heading')}</h1>

      {ref === undefined ? (
        <Alert variant="warning" title={t('shop.pendingService.invalid.title')} role="status">
          {t('shop.pendingService.invalid.body')}
        </Alert>
      ) : null}

      {status.isPending && ref !== undefined ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" label={t('shop.pendingService.loading')} />
        </div>
      ) : null}

      {status.isError ? (
        <Alert
          variant={isNotFound(status.error) ? 'warning' : 'danger'}
          title={
            isNotFound(status.error)
              ? t('shop.pendingService.invalid.title')
              : t('shop.pendingService.error.title')
          }
          role={isNotFound(status.error) ? 'status' : 'alert'}
        >
          {isNotFound(status.error)
            ? t('shop.pendingService.invalid.body')
            : t('shop.pendingService.error.body')}
        </Alert>
      ) : null}

      {status.isSuccess ? <PendingServiceRecord data={status.data} /> : null}
    </main>
  );
}

interface PendingServiceRecordProps {
  readonly data: ReturnType<typeof useNewServiceStatus>['data'];
}

function PendingServiceRecord({ data }: PendingServiceRecordProps): JSX.Element | null {
  const { t } = useTranslation();
  if (data === undefined) return null;

  const copy = pendingServiceCopy(data.state);
  const overdue = showsOverdueNotice(data.state, data.overdue);

  return (
    <div className="flex flex-col gap-4">
      <Card variant="glass" padding="md">
        <CardBody className="flex flex-col gap-3 p-0">
          <h2 className="text-base font-medium text-foreground">{t(copy.headingKey)}</h2>
          <p className="text-sm text-foreground/70">{t(copy.bodyKey)}</p>

          {/* The premise label appears only once a real premise has been matched
              to this record. Until then there is nothing true to show, and a
              placeholder would read as a confirmation we have not earned. */}
          {data.premiseLabel !== null ? (
            <p className="text-sm text-foreground">
              {t('shop.pendingService.premise', { premise: data.premiseLabel })}
            </p>
          ) : null}

          <dl className="flex flex-col gap-1 text-sm text-foreground/70">
            <div className="flex gap-2">
              <dt>{t('shop.pendingService.declaredOn')}</dt>
              <dd className="text-foreground">{data.declaredAt}</dd>
            </div>
            {data.expectedOccupancyDate !== null ? (
              <div className="flex gap-2">
                {/* Labelled as THEIR date, not as a utility commitment — we never
                    receive one, and presenting a guess as a promise is the thing
                    this whole epic is careful not to do. */}
                <dt>{t('shop.pendingService.yourExpectedDate')}</dt>
                <dd className="text-foreground">{data.expectedOccupancyDate}</dd>
              </div>
            ) : null}
            {/* US-14 — shown back so the customer has the reference to hand when
                they chase their city inspections office or their builder. */}
            {data.permitNumber !== null ? (
              <div className="flex gap-2">
                <dt>{t('shop.pendingService.permit')}</dt>
                <dd className="text-foreground">{data.permitNumber}</dd>
              </div>
            ) : null}
          </dl>
        </CardBody>
      </Card>

      {overdue ? (
        <Alert variant="warning" title={t('shop.pendingService.overdue.title')} role="status">
          {/* US-14 — with a permit in hand the advice is something the customer
              can actually do; without one it can only be "ask your utility",
              which is barely more useful than silence. */}
          {data.permitNumber === null
            ? t('shop.pendingService.overdue.body')
            : t('shop.pendingService.overdue.bodyWithPermit', { permit: data.permitNumber })}
        </Alert>
      ) : null}

      {copy.showsShopCta ? (
        <Link to="/" className="text-sm text-primary underline">
          {t('shop.pendingService.shopCta')}
        </Link>
      ) : (
        <p className="text-sm text-foreground/60">{t('shop.pendingService.checkBack')}</p>
      )}
    </div>
  );
}
